import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  RefreshCw, 
  Plus, 
  Calendar, 
  Clock,
  Pause,
  Play,
  Trash2,
  Edit
} from 'lucide-react';
import { PageContainer } from '../../components/ui/PageContainer';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { api } from '../../lib/api';

interface RecurringTransfer {
  id: string;
  recipientName: string;
  recipientDocument: string;
  recipientPixKey: string;
  amount: number;
  description?: string;
  frequency: 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY' | 'CUSTOM';
  dayOfWeek?: number;
  dayOfMonth?: number;
  customDays?: number;
  nextExecutionDate: string;
  lastExecutionDate?: string;
  status: 'ACTIVE' | 'PAUSED' | 'CANCELLED';
  executionCount: number;
  createdAt: string;
}

interface RecurringTransferForm {
  recipientName: string;
  recipientDocument: string;
  recipientPixKey: string;
  amount: string;
  description: string;
  frequency: string;
  dayOfWeek: string;
  dayOfMonth: string;
  customDays: string;
  startDate: string;
}

const initialForm: RecurringTransferForm = {
  recipientName: '',
  recipientDocument: '',
  recipientPixKey: '',
  amount: '',
  description: '',
  frequency: 'MONTHLY',
  dayOfWeek: '1',
  dayOfMonth: '1',
  customDays: '30',
  startDate: '',
};

export default function RecurringTransfers() {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState<RecurringTransfer | null>(null);
  const [form, setForm] = useState<RecurringTransferForm>(initialForm);

  // Fetch recurring transfers
  const { data: transfers, isLoading } = useQuery({
    queryKey: ['recurring-transfers'],
    queryFn: async () => {
      const response = await api.get('/banking/recurring');
      return response.data as RecurringTransfer[];
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/banking/recurring', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-transfers'] });
      setShowCreateModal(false);
      setForm(initialForm);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await api.put(`/banking/recurring/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-transfers'] });
      setEditingTransfer(null);
      setForm(initialForm);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/banking/recurring/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-transfers'] });
    },
  });

  // Toggle status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const response = await api.put(`/banking/recurring/${id}`, { status });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-transfers'] });
    },
  });

  const handleSubmit = () => {
    const data = {
      recipientName: form.recipientName,
      recipientDocument: form.recipientDocument.replace(/\D/g, ''),
      recipientPixKey: form.recipientPixKey,
      amount: Math.round(parseFloat(form.amount) * 100),
      description: form.description || undefined,
      frequency: form.frequency,
      dayOfWeek: form.frequency === 'WEEKLY' ? parseInt(form.dayOfWeek) : undefined,
      dayOfMonth: form.frequency === 'MONTHLY' || form.frequency === 'BIWEEKLY' ? parseInt(form.dayOfMonth) : undefined,
      customDays: form.frequency === 'CUSTOM' ? parseInt(form.customDays) : undefined,
      startDate: form.startDate || undefined,
    };

    if (editingTransfer) {
      updateMutation.mutate({ id: editingTransfer.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (transfer: RecurringTransfer) => {
    setEditingTransfer(transfer);
    setForm({
      recipientName: transfer.recipientName,
      recipientDocument: transfer.recipientDocument,
      recipientPixKey: transfer.recipientPixKey,
      amount: (transfer.amount / 100).toString(),
      description: transfer.description || '',
      frequency: transfer.frequency,
      dayOfWeek: transfer.dayOfWeek?.toString() || '1',
      dayOfMonth: transfer.dayOfMonth?.toString() || '1',
      customDays: transfer.customDays?.toString() || '30',
      startDate: '',
    });
    setShowCreateModal(true);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const getFrequencyLabel = (frequency: string) => {
    const labels: Record<string, string> = {
      WEEKLY: 'Semanal',
      BIWEEKLY: 'Quinzenal',
      MONTHLY: 'Mensal',
      CUSTOM: 'Personalizado',
    };
    return labels[frequency] || frequency;
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'error'> = {
      ACTIVE: 'success',
      PAUSED: 'warning',
      CANCELLED: 'error',
    };
    const labels: Record<string, string> = {
      ACTIVE: 'Ativo',
      PAUSED: 'Pausado',
      CANCELLED: 'Cancelado',
    };
    return <Badge variant={variants[status] || 'info'}>{labels[status] || status}</Badge>;
  };

  const weekDays = [
    { value: '0', label: 'Domingo' },
    { value: '1', label: 'Segunda' },
    { value: '2', label: 'Terça' },
    { value: '3', label: 'Quarta' },
    { value: '4', label: 'Quinta' },
    { value: '5', label: 'Sexta' },
    { value: '6', label: 'Sábado' },
  ];

  const monthDays = Array.from({ length: 28 }, (_, i) => ({
    value: (i + 1).toString(),
    label: (i + 1).toString(),
  }));

  return (
    <PageContainer
      title="Transferências Recorrentes"
      description="Configure transferências automáticas"
      icon={RefreshCw}
    >
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div />
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Nova Recorrência
        </Button>
      </div>

      {/* Transfers List */}
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-surface-100 mb-4">
            Transferências Programadas
          </h3>
          
          {isLoading ? (
            <div className="text-center py-8 text-surface-400">Carregando...</div>
          ) : transfers && transfers.length > 0 ? (
            <div className="space-y-4">
              {transfers.map((transfer) => (
                <div
                  key={transfer.id}
                  className="p-4 bg-surface-800 rounded-lg"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        transfer.status === 'ACTIVE' ? 'bg-success/20' : 'bg-surface-700'
                      }`}>
                        <RefreshCw className={`w-5 h-5 ${
                          transfer.status === 'ACTIVE' ? 'text-success' : 'text-surface-400'
                        }`} />
                      </div>
                      <div>
                        <p className="text-surface-100 font-medium">{transfer.recipientName}</p>
                        <p className="text-sm text-surface-400">{transfer.recipientPixKey}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-primary-400">
                        {formatCurrency(transfer.amount)}
                      </p>
                      {getStatusBadge(transfer.status)}
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-4 mb-3 text-sm">
                    <div>
                      <p className="text-surface-400">Frequência</p>
                      <p className="text-surface-100">{getFrequencyLabel(transfer.frequency)}</p>
                    </div>
                    <div>
                      <p className="text-surface-400">Próxima execução</p>
                      <p className="text-surface-100">{formatDate(transfer.nextExecutionDate)}</p>
                    </div>
                    <div>
                      <p className="text-surface-400">Última execução</p>
                      <p className="text-surface-100">
                        {transfer.lastExecutionDate ? formatDate(transfer.lastExecutionDate) : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-surface-400">Execuções</p>
                      <p className="text-surface-100">{transfer.executionCount}</p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {transfer.status === 'ACTIVE' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleStatusMutation.mutate({ id: transfer.id, status: 'PAUSED' })}
                      >
                        <Pause className="w-4 h-4 mr-1" />
                        Pausar
                      </Button>
                    )}
                    {transfer.status === 'PAUSED' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleStatusMutation.mutate({ id: transfer.id, status: 'ACTIVE' })}
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Retomar
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(transfer)}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteMutation.mutate(transfer.id)}
                    >
                      <Trash2 className="w-4 h-4 text-error" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-surface-400">
              Nenhuma transferência recorrente configurada
            </div>
          )}
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingTransfer(null);
          setForm(initialForm);
        }}
        title={editingTransfer ? 'Editar Recorrência' : 'Nova Recorrência'}
      >
        <div className="space-y-4">
          <Input
            label="Nome do destinatário"
            value={form.recipientName}
            onChange={(e) => setForm({ ...form, recipientName: e.target.value })}
            placeholder="Nome completo"
          />
          
          <Input
            label="CPF/CNPJ"
            value={form.recipientDocument}
            onChange={(e) => setForm({ ...form, recipientDocument: e.target.value })}
            placeholder="000.000.000-00"
          />
          
          <Input
            label="Chave Pix"
            value={form.recipientPixKey}
            onChange={(e) => setForm({ ...form, recipientPixKey: e.target.value })}
            placeholder="CPF, e-mail, telefone ou chave aleatória"
          />
          
          <Input
            label="Valor"
            type="number"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            placeholder="0,00"
            min="0"
            step="0.01"
          />
          
          <Input
            label="Descrição (opcional)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Ex: Aluguel, Mesada"
          />
          
          <Select
            label="Frequência"
            value={form.frequency}
            onChange={(e) => setForm({ ...form, frequency: e.target.value })}
            options={[
              { value: 'WEEKLY', label: 'Semanal' },
              { value: 'BIWEEKLY', label: 'Quinzenal' },
              { value: 'MONTHLY', label: 'Mensal' },
              { value: 'CUSTOM', label: 'Personalizado' },
            ]}
          />
          
          {form.frequency === 'WEEKLY' && (
            <Select
              label="Dia da semana"
              value={form.dayOfWeek}
              onChange={(e) => setForm({ ...form, dayOfWeek: e.target.value })}
              options={weekDays}
            />
          )}
          
          {(form.frequency === 'MONTHLY' || form.frequency === 'BIWEEKLY') && (
            <Select
              label="Dia do mês"
              value={form.dayOfMonth}
              onChange={(e) => setForm({ ...form, dayOfMonth: e.target.value })}
              options={monthDays}
            />
          )}
          
          {form.frequency === 'CUSTOM' && (
            <Input
              label="Intervalo em dias"
              type="number"
              value={form.customDays}
              onChange={(e) => setForm({ ...form, customDays: e.target.value })}
              placeholder="30"
              min="1"
            />
          )}
          
          {!editingTransfer && (
            <Input
              label="Data de início (opcional)"
              type="date"
              value={form.startDate}
              onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              min={new Date().toISOString().split('T')[0]}
            />
          )}

          <div className="flex gap-4 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setShowCreateModal(false);
                setEditingTransfer(null);
                setForm(initialForm);
              }}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                !form.recipientName || 
                !form.recipientPixKey || 
                !form.amount ||
                createMutation.isPending ||
                updateMutation.isPending
              }
              className="flex-1"
            >
              {createMutation.isPending || updateMutation.isPending 
                ? 'Salvando...' 
                : editingTransfer ? 'Salvar' : 'Criar'
              }
            </Button>
          </div>
        </div>
      </Modal>
    </PageContainer>
  );
}
